if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            /* Pe mobil lista principală poate rămâne cu scrollLeft ≠ 0 → se vede alt cadru decât .is-active; prima miniatură pare „deplasată”. */
            this.syncMainViewerScrollToActiveSlide();
            this.syncThumbnailScrollToActive();
          });
        });
      }

      /** Aliniază scroll-ul galeriei principale cu slide-ul .is-active (același comportament ca setActiveMedia în Dawn). */
      syncMainViewerScrollToActiveSlide() {
        const viewerRoot = this.elements?.viewer;
        if (!viewerRoot) return;
        const slideList = viewerRoot.querySelector('ul[id^="Slider-Gallery-"], ul.product__media-list.slider--mobile');
        if (!slideList) return;
        const active = viewerRoot.querySelector('.product__media-item.is-active');
        if (!active) return;
        const maxScroll = Math.max(0, slideList.scrollWidth - slideList.clientWidth);
        const left = Math.max(0, Math.min(maxScroll, active.offsetLeft));
        slideList.scrollTo({ left });
      }

      /** Aliniază scroll-ul benzii de thumbnails cu thumb-ul marcat aria-current (fără isSlideVisible — flex center strică offsetLeft vs viewport). */
      syncThumbnailScrollToActive() {
        if (!this.elements?.thumbnails) return;
        const btn = this.elements.thumbnails.querySelector('.thumbnail[aria-current="true"]');
        const li = btn && btn.closest('[data-target]');
        if (li) this.scrollThumbnailRowToReveal(li);
      }

      scrollThumbnailRowToReveal(thumbnailLi) {
        const sliderHost = this.elements.thumbnails;
        if (!sliderHost || !thumbnailLi) return;
        const slider = sliderHost.slider;
        if (!slider) return;

        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        if (maxScroll <= 1) {
          slider.scrollLeft = 0;
          return;
        }

        /*
         * NU folosi offsetLeft — cu justify-content:center / flex, offsetParent poate fi
         * un ancestor fals → offsetLeft enorm → scrollLeft clamp la maxScroll → primele
         * miniaturi rămân în stânga, „inaccesibile”.
         * Poziția în conținutul scrollabil: distanța față de viewport-ul listei + scroll curent.
         */
        const thumbRect = thumbnailLi.getBoundingClientRect();
        const sliderRect = slider.getBoundingClientRect();
        const thumbLeftInContent =
          thumbRect.left - sliderRect.left + slider.scrollLeft;

        /* Aliniem marginea stângă a thumb-ului la marginea stângă a zonei vizibile (ca Dawn), fără „centrare” care împinge spre dreapta. */
        let target = thumbLeftInContent;
        target = Math.max(0, Math.min(maxScroll, target));
        slider.scrollTo({ left: target });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.scrollThumbnailRowToReveal(thumbnail);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
